import * as React from 'react';
import { api, ApiError } from '@/lib/api-client';
import { useI18n, type TranslationKey } from '@/lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, DropdownSelect, ErrorState, PageHeader, SkeletonCard } from '@/components/ui';

// NOTIF-04: self-service email opt-out per event type + daily digest toggle.
// In-app (bell) notifications always stay on — this page only controls
// whether an email also goes out for a given event.
interface EmailPreference {
  eventType: string;
  enabled: boolean;
}
interface Preferences {
  digestFrequency: 'off' | 'daily';
  email: EmailPreference[];
}

const EVENT_LABEL_KEYS: Record<string, TranslationKey> = {
  ticket_created: 'preferences.createdEvent',
  ticket_assigned: 'preferences.assignedEvent',
  ticket_group_assigned: 'preferences.groupAssignedEvent',
  group_member_added: 'preferences.groupMemberAddedEvent',
  ticket_reply: 'preferences.replyEvent',
  ticket_note: 'preferences.noteEvent',
  sla_warning: 'preferences.slaWarningEvent',
  sla_breach: 'preferences.slaBreachEvent',
  sla_escalation: 'preferences.slaEscalationEvent',
  mention: 'preferences.mentionEvent',
  pending_input: 'preferences.pendingInputEvent',
  ticket_resolved: 'preferences.resolvedEvent',
};
type Translator = ReturnType<typeof useI18n>['t'];

function message(error: unknown, t: Translator) {
  return error instanceof ApiError ? error.message : t('common.requestFailed');
}

export function NotificationPreferencesPage() {
  const { t } = useI18n();
  const [preferences, setPreferences] = React.useState<Preferences | null>(null);
  const [error, setError] = React.useState('');
  const [savingKey, setSavingKey] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setError('');
    try {
      const response = await api.get<{ data: Preferences }>('/notifications/preferences');
      setPreferences(response.data);
    } catch (e) {
      setError(message(e, t));
    }
  }, [t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function toggleEmail(eventType: string, enabled: boolean) {
    setSavingKey(eventType);
    setError('');
    try {
      const response = await api.put<{ data: Preferences }>('/notifications/preferences/email', {
        eventType,
        enabled,
      });
      setPreferences(response.data);
    } catch (e) {
      setError(message(e, t));
    } finally {
      setSavingKey(null);
    }
  }

  async function changeDigest(digestFrequency: 'off' | 'daily') {
    setSavingKey('digest');
    setError('');
    try {
      const response = await api.put<{ data: Preferences }>('/notifications/preferences/digest', {
        digestFrequency,
      });
      setPreferences(response.data);
    } catch (e) {
      setError(message(e, t));
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={[{ label: t('nav.settings'), to: '/settings' }, { label: t('preferences.title') }]}
        title={t('preferences.title')}
        description={t('preferences.description')}
        className="mb-0"
      />

      {error && <ErrorState message={error} onRetry={() => void load()} />}

      {!preferences ? (
        <div className="space-y-6">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{t('preferences.emailDigest')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-3 text-sm text-ink-subtle">
                {t('preferences.dailySummary')}
              </p>
              <div className="max-w-xs">
                <DropdownSelect
                  options={[
                    { value: 'off', label: t('preferences.digestOff') },
                    { value: 'daily', label: t('preferences.digestDaily') },
                  ]}
                  value={preferences.digestFrequency}
                  disabled={savingKey === 'digest'}
                  onChange={(value) => void changeDigest(value as 'off' | 'daily')}
                  searchable={false}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('preferences.emailNotifications')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {preferences.digestFrequency === 'daily' && (
                <p className="text-sm text-warning-700">
                  {t('preferences.digestWarning')}
                </p>
              )}
              {preferences.email.map((pref) => (
                <label key={pref.eventType} className="flex items-center justify-between gap-4 py-1">
                  <span className="text-sm text-ink-muted">{EVENT_LABEL_KEYS[pref.eventType] ? t(EVENT_LABEL_KEYS[pref.eventType]) : pref.eventType}</span>
                  <input
                    type="checkbox"
                    checked={pref.enabled}
                    disabled={savingKey === pref.eventType}
                    onChange={(e) => void toggleEmail(pref.eventType, e.target.checked)}
                    className="h-4 w-4 rounded border-line-strong text-brand-600 focus:ring-brand-500"
                  />
                </label>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
